// Record-time capture of invisible / near-invisible text.
//
// The deterministic trace compiler can read INLINE color/background off the
// recorded DOM, but most real apps color text with classes, CSS-in-JS, or
// theming — none of which are visible without a browser. This module closes
// that gap at the only place it can be closed: record time. It walks the live
// DOM, reads the browser's already-resolved getComputedStyle() foreground and
// background for text elements, and — for any whose colors effectively match
// (white-on-white validation errors, invisible prices) — emits an
// `hb-contrast` custom event carrying the rrweb node id + the two colors.
// The compiler recomputes the contrast ratio deterministically from those.
//
// Invariants (matching the CSS-snapshot / error-capture subsystems): never
// throws into the recording path, bounded work (element + emit + scan caps),
// and each node is reported at most once per session.

export const HB_CONTRAST_TAG = 'hb-contrast';
// Clipped/cut-off text: content overflowing an `overflow:hidden` box with no
// ellipsis (a truncated total, a spilled label). Same record-time sweep as
// contrast; the compiler emits a clipped_content row.
export const HB_CLIP_TAG = 'hb-clip';
// A failed asset load (broken <img>/<script>/<link>/media). Emitted by the
// tracker's capture-phase error listener (it owns the rrweb mirror).
export const HB_BROKEN_ASSET_TAG = 'hb-broken-asset';
// Two text elements whose rendered glyphs collide (content printed on top of
// content) due to a layout bug — a price chip over a title, a button dragged
// over an input. Same record-time sweep as contrast; the compiler emits a
// content_overlap row. Intentional layering (modals, dropdowns, toasts, FABs,
// sticky bars) is excluded at detection time so only unintended collisions fire.
export const HB_OVERLAP_TAG = 'hb-overlap';
// A table whose cells in one column do not share a left edge — a column
// misalignment bug (a shifted value column, numbers under the wrong headers).
// The compiler emits a column_misalign row.
export const HB_MISALIGN_TAG = 'hb-misalign';

export interface ContrastPayload {
    // rrweb mirror id of the text-bearing element.
    id: number;
    // getComputedStyle colors, as the browser resolved them (e.g. "rgb(255, 255, 255)").
    fg: string;
    bg: string;
    // Precomputed WCAG ratio (the compiler recomputes and gates independently).
    ratio: number;
    // Short text sample, for the trace detail line.
    sample: string;
}

export interface ClipPayload {
    // rrweb mirror id of the clipped element.
    id: number;
    // Which axis overflowed its clipped box.
    axis: 'x' | 'y';
    // Short text sample of the clipped content.
    sample: string;
}

export interface OverlapPayload {
    // rrweb mirror id of the element painted ON TOP (later in document order).
    id: number;
    // rrweb mirror id of the element it collides with underneath.
    id2: number;
    // Short text samples of both colliding elements.
    sample: string;
    sample2: string;
}

export interface BrokenAssetPayload {
    // rrweb mirror id of the failed element.
    id: number;
    // Element kind (img/script/link/...), for the trace label.
    tag: string;
    // The URL that failed to load.
    url: string;
}

export interface MisalignPayload {
    // rrweb mirror id of the misaligned <table>.
    id: number;
    // Human-readable description of the misaligned column.
    sample: string;
}

// Ratio at or below this means the text is effectively the same color as its
// background. Matches the compiler's INVISIBLE_CONTRAST_MAX. Far below the
// 4.5:1 accessibility bar: we hunt invisible text, not merely low-contrast.
const INVISIBLE_CONTRAST_MAX = 1.25;
// Bounds so a pathological page cannot stall recording or flood the stream.
const MAX_ELEMENTS_PER_SCAN = 4000;
const MAX_EMITS_PER_SESSION = 50;
const MAX_SCANS = 15;
const SCAN_INTERVAL_MS = 1500;
const MIN_TEXT_LEN = 2;
const MAX_SAMPLE_LEN = 100;
// Content must exceed its clipped box by more than this (px) to count — guards
// against sub-pixel rounding noise.
const CLIP_SLOP_PX = 2;
// Overlap detection bounds. Pairwise text-rect comparison is O(n²), so cap the
// candidate set; real collisions involve a handful of headline/price/label
// elements, not a whole paragraph corpus.
const MAX_OVERLAP_CANDIDATES = 250;
const MAX_TEXT_LINES = 12;
// A collision counts only when two text LINE rects intersect by at least this
// fraction of the smaller line AND by at least this many px on each axis —
// half a line of text covered is unambiguous; a 1px touch of adjacent text is
// not. Precision-first: better to miss a marginal overlap than flag normal
// adjacency.
const OVERLAP_MIN_FRACTION = 0.5;
const OVERLAP_MIN_PX = 3;
// Deliberate stacking: a positioned element with a z-index at or above this is
// an intentional overlay (menu, popover, sticky header), not a layout bug.
const OVERLAY_ZINDEX = 5;
// A table column whose cell left-edges span more than this many px is
// misaligned. Well past padding/border sub-pixel noise (table layout pins every
// cell in a column to the same left edge), so only a real shift trips it.
const MISALIGN_MIN_PX = 12;
const MAX_TABLES_PER_SCAN = 50;
// Only flag a failed image big enough to be a visible defect — skip 1x1
// tracking pixels and tiny decorative glyphs that happen to 404.
const MIN_BROKEN_IMG_PX = 24;
const MAX_IMAGES_PER_SCAN = 300;

interface RGBA {
    r: number;
    g: number;
    b: number;
    a: number;
}

function parseRgb(input: string): RGBA | null {
    const s = input.trim().toLowerCase();
    if (!s || s === 'transparent') return null;
    const open = s.indexOf('(');
    const close = s.indexOf(')');
    if (open === -1 || close === -1) return null;
    const parts = s.slice(open + 1, close).split(/[,/\s]+/).filter(Boolean);
    if (parts.length < 3) return null;
    const r = parseFloat(parts[0]);
    const g = parseFloat(parts[1]);
    const b = parseFloat(parts[2]);
    const a = parts.length >= 4 ? parseFloat(parts[3]) : 1;
    if ([r, g, b, a].some((n) => Number.isNaN(n))) return null;
    return { r, g, b, a };
}

function compositeOver(fg: RGBA, bg: RGBA): RGBA {
    const a = fg.a;
    return {
        r: fg.r * a + bg.r * (1 - a),
        g: fg.g * a + bg.g * (1 - a),
        b: fg.b * a + bg.b * (1 - a),
        a: 1,
    };
}

function channelLuminance(c: number): number {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

function relativeLuminance(c: RGBA): number {
    return 0.2126 * channelLuminance(c.r) + 0.7152 * channelLuminance(c.g) + 0.0722 * channelLuminance(c.b);
}

export function contrastRatio(fg: RGBA, bg: RGBA): number {
    const effectiveFg = fg.a < 1 ? compositeOver(fg, bg) : fg;
    const l1 = relativeLuminance(effectiveFg);
    const l2 = relativeLuminance(bg);
    return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

// The element's OWN direct text (not descendants), so contrast is attributed to
// the element that actually paints the glyphs.
function directText(el: Element): string {
    let out = '';
    const nodes = el.childNodes;
    for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        if (n.nodeType === 3 /* Text */) out += n.textContent ?? '';
    }
    return out.trim();
}

// Resolve the effective background by walking ancestors for the first opaque
// background-color. Returns null when a background image/gradient is in play
// (its color is not knowable from computed style) so we never guess.
function resolveBackground(el: Element, win: Window): RGBA | null {
    let cur: Element | null = el;
    let hops = 0;
    while (cur && hops < 50) {
        const cs = win.getComputedStyle(cur);
        if (cs.backgroundImage && cs.backgroundImage !== 'none') return null;
        const bg = parseRgb(cs.backgroundColor);
        if (bg && bg.a >= 0.5) return bg;
        cur = cur.parentElement;
        hops++;
    }
    // Nothing opaque up the chain and no image: the canvas shows through as the
    // browser default (white).
    return { r: 255, g: 255, b: 255, a: 1 };
}

function isVisible(el: Element, win: Window): boolean {
    const cs = win.getComputedStyle(el);
    if (cs.visibility === 'hidden' || cs.visibility === 'collapse' || cs.display === 'none') return false;
    if (parseFloat(cs.opacity || '1') === 0) return false;
    // getClientRects is empty for elements not laid out (display:none ancestors,
    // detached, etc.).
    return (el as HTMLElement).getClientRects().length > 0;
}

// Screen-reader-only / visually-hidden a11y text (Tailwind `.sr-only`, Bootstrap
// `.visually-hidden`, etc.). These boxes are intentionally 1×1 + overflow:hidden
// (or clipped to nothing) so assistive tech still gets a heading while sighted
// users see decorative ASCII / icons. That pattern ALWAYS trips scrollWidth >
// clientWidth — it is not a product clip bug. Real clipped-content defects are
// visible-sized containers truncating text.
function isVisuallyHidden(el: Element, cs: CSSStyleDeclaration): boolean {
    const h = el as HTMLElement;
    if (
        h.clientWidth > 0 &&
        h.clientWidth <= 2 &&
        h.clientHeight > 0 &&
        h.clientHeight <= 2
    ) {
        return true;
    }
    // Legacy clip: rect(0, 0, 0, 0)
    const clip = cs.clip || '';
    if (/rect\s*\(\s*0(px)?\s*,\s*0(px)?\s*,\s*0(px)?\s*,\s*0(px)?\s*\)/i.test(clip)) {
        return true;
    }
    // Modern Tailwind sr-only: clip-path: inset(50%)
    const clipPath =
        (cs as CSSStyleDeclaration & { clipPath?: string }).clipPath ||
        (typeof cs.getPropertyValue === 'function' ? cs.getPropertyValue('clip-path') : '') ||
        '';
    if (/inset\s*\(\s*50%/i.test(clipPath)) {
        return true;
    }
    return false;
}

// Detect content clipped by an `overflow:hidden`/`clip` box (a truncated total,
// a label spilling out) that has NO ellipsis affordance — deliberate ellipsis
// truncation is not a defect. Returns the overflowing axis or null.
function clippedAxis(el: Element, cs: CSSStyleDeclaration): 'x' | 'y' | null {
    if (cs.textOverflow === 'ellipsis') return null;
    // CSS line-clamp (Tailwind `line-clamp-*`) is deliberate multi-line
    // truncation: it sets overflow:hidden and always overflows vertically.
    const lineClamp =
        (cs as CSSStyleDeclaration & { webkitLineClamp?: string }).webkitLineClamp ||
        (typeof cs.getPropertyValue === 'function'
            ? cs.getPropertyValue('-webkit-line-clamp') || cs.getPropertyValue('line-clamp')
            : '');
    if (lineClamp && lineClamp !== 'none') return null;
    if (isVisuallyHidden(el, cs)) return null;
    const h = el as HTMLElement;
    const clipsX = cs.overflowX === 'hidden' || cs.overflowX === 'clip';
    const clipsY = cs.overflowY === 'hidden' || cs.overflowY === 'clip';
    if (clipsX && h.clientWidth > 0 && h.scrollWidth > h.clientWidth + CLIP_SLOP_PX) return 'x';
    if (clipsY && h.clientHeight > 0 && h.scrollHeight > h.clientHeight + CLIP_SLOP_PX) return 'y';
    return null;
}

// The tight rectangles of an element's OWN direct text (one per rendered line),
// via a Range over each direct text node. Using glyph rects — not the element
// box — means a child positioned over its parent's text collides on the actual
// text, and a parent that merely CONTAINS a child box does not.
function directTextLineRects(el: Element, doc: Document): DOMRect[] {
    const out: DOMRect[] = [];
    const nodes = el.childNodes;
    for (let i = 0; i < nodes.length && out.length < MAX_TEXT_LINES; i++) {
        const n = nodes[i];
        if (n.nodeType !== 3 /* Text */) continue;
        if (!(n.textContent ?? '').trim()) continue;
        try {
            const range = doc.createRange();
            range.selectNodeContents(n);
            const rects = range.getClientRects();
            for (let k = 0; k < rects.length && out.length < MAX_TEXT_LINES; k++) {
                const r = rects[k];
                if (r.width > 0 && r.height > 0) out.push(r);
            }
        } catch {
            /* Range over a detached/odd node — skip it. */
        }
    }
    return out;
}

// True when any line rect of A intersects any line rect of B past both the
// per-axis and area thresholds.
function textLinesCollide(a: DOMRect[], b: DOMRect[]): boolean {
    for (let i = 0; i < a.length; i++) {
        for (let j = 0; j < b.length; j++) {
            const r1 = a[i];
            const r2 = b[j];
            const ix = Math.min(r1.right, r2.right) - Math.max(r1.left, r2.left);
            const iy = Math.min(r1.bottom, r2.bottom) - Math.max(r1.top, r2.top);
            if (ix <= OVERLAP_MIN_PX || iy <= OVERLAP_MIN_PX) continue;
            const inter = ix * iy;
            const smaller = Math.min(r1.width * r1.height, r2.width * r2.height);
            if (smaller > 0 && inter / smaller >= OVERLAP_MIN_FRACTION) return true;
        }
    }
    return false;
}

// Deliberate overlay context: the element (or an ancestor) is viewport-pinned
// (fixed/sticky), a dialog/menu/tooltip role, or positioned with a real
// z-index. Layering such content on top of the page is intended — never a bug.
function isFloatingOverlay(el: Element, win: Window): boolean {
    let cur: Element | null = el;
    let hops = 0;
    while (cur && hops < 12) {
        const cs = win.getComputedStyle(cur);
        if (cs.position === 'fixed' || cs.position === 'sticky') return true;
        if (cs.position === 'absolute' || cs.position === 'relative') {
            const z = parseInt(cs.zIndex, 10);
            if (Number.isFinite(z) && z >= OVERLAY_ZINDEX) return true;
        }
        const role = cur.getAttribute ? cur.getAttribute('role') ?? '' : '';
        if (/^(dialog|alertdialog|tooltip|menu|listbox|combobox)$/.test(role)) return true;
        if (cur.tagName === 'DIALOG') return true;
        cur = cur.parentElement;
        hops++;
    }
    return false;
}

interface OverlapCandidate {
    id: number;
    order: number;
    floating: boolean;
    lines: DOMRect[];
    sample: string;
}

export class ContrastCapture {
    private emittedIds = new Set<number>();
    private clippedIds = new Set<number>();
    private overlapPairs = new Set<string>();
    private misalignedIds = new Set<number>();
    private brokenAssetUrls = new Set<string>();
    private emitCount = 0;
    private scanCount = 0;
    private timer: ReturnType<typeof setInterval> | null = null;

    constructor(
        private emit: (payload: ContrastPayload) => void,
        private getId: (node: Node) => number,
        private emitClip?: (payload: ClipPayload) => void,
        private emitOverlap?: (payload: OverlapPayload) => void,
        private emitMisalign?: (payload: MisalignPayload) => void,
        private emitBrokenAsset?: (payload: BrokenAssetPayload) => void,
    ) {}

    // Initial scan + a bounded periodic re-scan: invisible text often appears
    // only after interaction (a validation error on submit), so one pass is not
    // enough. Per-node dedupe keeps repeated scans cheap.
    start(doc: Document, win: Window): void {
        try {
            this.scan(doc, win);
            // Resource-settled rescan: `load` fires once every image/script/etc
            // has loaded OR failed. It is the deterministic moment to catch
            // assets that 404'd during initial load — the error listener may
            // have attached too late to hear them, and the periodic timer may
            // not tick between settle and the next captured moment.
            if (doc.readyState !== 'complete') {
                win.addEventListener(
                    'load',
                    () => {
                        try {
                            this.scan(doc, win);
                        } catch {
                            /* scanning is best-effort */
                        }
                    },
                    { once: true },
                );
            }
            if (this.timer) clearInterval(this.timer);
            this.timer = setInterval(() => {
                try {
                    if (this.scanCount >= MAX_SCANS || this.emitCount >= MAX_EMITS_PER_SESSION) {
                        this.dispose();
                        return;
                    }
                    this.scan(doc, win);
                } catch {
                    /* scanning is best-effort */
                }
            }, SCAN_INTERVAL_MS);
        } catch {
            /* never throw into the recording path */
        }
    }

    // Walk text-bearing elements and emit invisible ones. Exposed for tests.
    scan(doc: Document, win: Window): void {
        this.scanCount++;
        const root = doc.body ?? doc.documentElement;
        if (!root) return;
        const all = root.querySelectorAll('*');
        const limit = Math.min(all.length, MAX_ELEMENTS_PER_SCAN);
        const overlapCandidates: OverlapCandidate[] = [];
        for (let i = 0; i < limit; i++) {
            if (this.emitCount >= MAX_EMITS_PER_SESSION) break;
            const el = all[i];
            const text = directText(el);
            if (text.length < MIN_TEXT_LEN || !/[\p{L}\p{N}]/u.test(text)) continue;

            const id = this.getId(el);
            if (id < 0) continue;
            if (!isVisible(el, win)) continue;

            const cs = win.getComputedStyle(el);

            // Content-collision candidate: any visible element with its own text.
            // Collected here (before the contrast/clip early-continues) so every
            // text element is considered for overlap.
            if (this.emitOverlap && overlapCandidates.length < MAX_OVERLAP_CANDIDATES) {
                const lines = directTextLineRects(el, doc);
                if (lines.length > 0) {
                    overlapCandidates.push({
                        id,
                        order: i,
                        floating: isFloatingOverlay(el, win),
                        lines,
                        sample: text.slice(0, MAX_SAMPLE_LEN),
                    });
                }
            }

            // Clipped/cut-off content (independent of contrast).
            if (this.emitClip && !this.clippedIds.has(id)) {
                const axis = clippedAxis(el, cs);
                if (axis) {
                    this.clippedIds.add(id);
                    this.emitCount++;
                    this.emitClip({ id, axis, sample: text.slice(0, MAX_SAMPLE_LEN) });
                }
            }

            if (this.emittedIds.has(id)) continue;
            const fg = parseRgb(cs.color);
            if (!fg) continue;
            const bg = resolveBackground(el, win);
            if (!bg) continue;

            const ratio = contrastRatio(fg, bg);
            if (ratio > INVISIBLE_CONTRAST_MAX) continue;

            this.emittedIds.add(id);
            this.emitCount++;
            this.emit({
                id,
                fg: `rgb(${Math.round(fg.r)}, ${Math.round(fg.g)}, ${Math.round(fg.b)})`,
                bg: `rgb(${Math.round(bg.r)}, ${Math.round(bg.g)}, ${Math.round(bg.b)})`,
                ratio,
                sample: text.slice(0, MAX_SAMPLE_LEN),
            });
        }

        if (this.emitOverlap) this.detectOverlaps(overlapCandidates);
        if (this.emitMisalign) this.detectMisalignedTables(doc, win);
        if (this.emitBrokenAsset) this.detectBrokenImages(doc, win);
    }

    // Catch-up scan for failed images. The tracker's capture-phase `error`
    // listener only hears failures that happen AFTER it attaches, so any image
    // that 404'd during initial load (the hero, above-the-fold art) is missed —
    // the error already fired. This polls the live DOM instead: a loaded-but-
    // zero-size image is a definitively broken one, regardless of when it failed.
    private detectBrokenImages(doc: Document, win: Window): void {
        if (!this.emitBrokenAsset) return;
        const imgs = doc.querySelectorAll('img');
        const limit = Math.min(imgs.length, MAX_IMAGES_PER_SCAN);
        for (let i = 0; i < limit; i++) {
            if (this.emitCount >= MAX_EMITS_PER_SESSION) return;
            const img = imgs[i] as HTMLImageElement;
            const url = img.currentSrc || img.src || img.getAttribute('src') || '';
            if (!url || this.brokenAssetUrls.has(url)) continue;
            // complete + naturalWidth 0 = the load finished and failed (a
            // still-loading image has complete === false).
            if (!img.complete || img.naturalWidth !== 0) continue;
            if (!isVisible(img, win)) continue;
            // A broken image often COLLAPSES to its alt-text size (the browser
            // ignores the width/height attrs once the decode fails), so the
            // rendered box understates the real image. Judge intent by the max
            // of the rendered box and the declared width/height — still small
            // enough to skip 1x1 tracking pixels, but keeps a collapsed hero.
            const rect = img.getBoundingClientRect();
            const attrW = parseInt(img.getAttribute('width') || '', 10);
            const attrH = parseInt(img.getAttribute('height') || '', 10);
            const w = Math.max(rect.width, Number.isFinite(attrW) ? attrW : 0);
            const h = Math.max(rect.height, Number.isFinite(attrH) ? attrH : 0);
            if (w < MIN_BROKEN_IMG_PX || h < MIN_BROKEN_IMG_PX) continue;
            // Emit even when the rrweb mirror has not yet assigned an id (id = -1
            // early in the session). The failed URL is the identity that matters
            // for the broken_asset signal; the compiler tolerates a missing id.
            const id = this.getId(img);
            this.brokenAssetUrls.add(url);
            this.emitCount++;
            this.emitBrokenAsset({ id, tag: 'img', url });
        }
    }

    // Column-misalignment pass: for each visible <table>, the cells in a given
    // column should share a left edge (table layout guarantees it). When one
    // column's cell left-edges span more than the threshold, a positioning bug
    // shifted that column. Cells with colspan/rowspan are skipped (merges break
    // alignment legitimately), and irregular tables are ignored entirely.
    private detectMisalignedTables(doc: Document, win: Window): void {
        if (!this.emitMisalign) return;
        const tables = doc.querySelectorAll('table');
        const limit = Math.min(tables.length, MAX_TABLES_PER_SCAN);
        for (let t = 0; t < limit; t++) {
            if (this.emitCount >= MAX_EMITS_PER_SESSION) return;
            const table = tables[t];
            const id = this.getId(table);
            if (id < 0 || this.misalignedIds.has(id)) continue;
            if (!isVisible(table, win)) continue;

            const rows = table.querySelectorAll('tr');
            if (rows.length < 2) continue;

            const colLefts = new Map<number, number[]>();
            let irregular = false;
            for (let r = 0; r < rows.length && !irregular; r++) {
                const cells = rows[r].children;
                for (let c = 0; c < cells.length; c++) {
                    const cell = cells[c] as HTMLElement;
                    if (cell.tagName !== 'TD' && cell.tagName !== 'TH') continue;
                    const colspan = parseInt(cell.getAttribute('colspan') || '1', 10);
                    const rowspan = parseInt(cell.getAttribute('rowspan') || '1', 10);
                    if (colspan !== 1 || rowspan !== 1) { irregular = true; break; }
                    const rect = cell.getBoundingClientRect();
                    if (rect.width <= 0 || rect.height <= 0) continue;
                    const arr = colLefts.get(c) ?? [];
                    arr.push(rect.left);
                    colLefts.set(c, arr);
                }
            }
            if (irregular) continue;

            let worst = 0;
            let worstCol = -1;
            for (const [c, xs] of colLefts) {
                if (xs.length < 2) continue;
                const spread = Math.max(...xs) - Math.min(...xs);
                if (spread > worst) { worst = spread; worstCol = c; }
            }
            if (worst >= MISALIGN_MIN_PX && worstCol >= 0) {
                this.misalignedIds.add(id);
                this.emitCount++;
                this.emitMisalign({ id, sample: `column ${worstCol + 1} cells misaligned by ${Math.round(worst)}px` });
            }
        }
    }

    // Pairwise text-collision pass over the scan's candidates. A pair fires only
    // when neither element is a deliberate overlay and their glyph rects
    // genuinely intersect. The element later in document order is reported as
    // the one painted on top.
    private detectOverlaps(candidates: OverlapCandidate[]): void {
        if (!this.emitOverlap) return;
        for (let i = 0; i < candidates.length; i++) {
            for (let j = i + 1; j < candidates.length; j++) {
                if (this.emitCount >= MAX_EMITS_PER_SESSION) return;
                const a = candidates[i];
                const b = candidates[j];
                // Intentional layering (either side an overlay) is not a bug.
                if (a.floating || b.floating) continue;
                if (!textLinesCollide(a.lines, b.lines)) continue;
                const key = a.id < b.id ? `${a.id}-${b.id}` : `${b.id}-${a.id}`;
                if (this.overlapPairs.has(key)) continue;
                this.overlapPairs.add(key);
                this.emitCount++;
                const top = a.order >= b.order ? a : b;
                const other = top === a ? b : a;
                this.emitOverlap({ id: top.id, id2: other.id, sample: top.sample, sample2: other.sample });
            }
        }
    }

    dispose(): void {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
    }
}
